// React
import { Fragment, useContext } from "react"

// Components
import { CommandButton, Loading } from "components"
import { ActionButton } from "../components"
import { DeleteModal, InputSection } from "./components"

// Contexts
import { AccountProvider } from "contexts"
import { BillContext } from "./contexts"

// Form
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { useApi, useToggle } from "utils"
import { FormType, useDetail, validationSchema } from "./utils"

export default function EditPaymentBill(): JSX.Element {
  // Hooks
  const { payment_bill_id } = useParams()
  const { data, isLoading } = useDetail(payment_bill_id!)

  if (isLoading || !data) {
    return <Loading loading={isLoading} />
  }

  return (
    <BillContext.Provider value={data}>
      <AccountProvider>
        <FormSection />
      </AccountProvider>
    </BillContext.Provider>
  )
}

function FormSection(): JSX.Element {
  // Hooks
  const api = useApi()
  const data = useContext(BillContext)
  const navigate = useNavigate()
  const { payment_bill_id } = useParams()

  // Vars
  const databill = data.databill

  // Form
  const defaultValues: FormType = {
    _balance: 0,
    coa_id: data.coa_id,
    memo: data.memo,
    deposit_amount: data.deposit_amount,
    payment_amount: data.payment_amount,
    referensi: data.referensi,
    trx_code: data.trx_code,
    transaction_date: data.transaction_date,
    vendor_id: databill.vendor_id
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const onSubmit = (value: FormType) => {
    // Vars
    const final_value = {
      payment_bill_id,
      bill_id: data.bill_id,
      trx_code: value.trx_code,
      vendor_id: value.vendor_id,
      location_id: databill.location_id,
      address: databill.address,
      memo: value.memo,
      transaction_date: value.transaction_date,
      referensi: value.referensi,
      coa_id: value.coa_id,
      deposit_amount: value.deposit_amount,
      payment_amount: value.payment_amount
    }
    const isBelowBalance: boolean = databill.balance - value.deposit_amount - value.payment_amount < 0

    return new Promise<void>((resolve) => {
      // Functions
      const submitForm = (): void => {
        toast.promise(
          api.put("/paymentbill/updatepayment", final_value),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.detail?.message ?? err.response.data.message
          }
        ).then(() => {
          navigate(-1)
        }).catch(() => {}).finally(resolve)
      }

      if (isBelowBalance) {
        toast.error("Payment Deposit & Payment Amount cannot be more than Balance Bill")
        return resolve()
      }

      submitForm()
    })
  }

  return (
    <FormProvider {...methods}>
      <InputSection
        buttonsection={(
          <ActionButton>
            <section className="flex flex-wrap gap-3">
              <CommandButton actiontype="help" />
              <CommandButton actiontype="print" />
              <CommandButton actiontype="email" />
              <CommandButton actiontype="journal" />
              <CommandButton actiontype="attachment" />
            </section>

            <section className="flex flex-wrap gap-3">
              {data.approve_status === 1 && <Delete />}

              {data.approve_status !== 4 && (
                <CommandButton
                  actiontype="save"
                  loading={methods.formState.isSubmitting ? "true" : undefined}
                  onClick={methods.handleSubmit(onSubmit)}
                />
              )}
            </section>
          </ActionButton>
        )}
      />
    </FormProvider>
  )
}

function Delete(): JSX.Element {
  // Hooks
  const navigate = useNavigate()
  const { payment_bill_id } = useParams()
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <CommandButton
        actiontype="delete"
        onClick={toggle}
      />

      {isActive && (
        <DeleteModal
          payment_bill_id={payment_bill_id!}
          toggle={toggle}
          onSuccess={() => navigate(-1)}
        />
      )}
    </Fragment>
  )
}